import * as React from "react";
import {
  Input,
  Field,
  Spinner,
  Badge,
  Caption1,
  Body1,
  tokens,
  makeStyles,
  mergeClasses,
} from "@fluentui/react-components";
import { Search20Regular } from "@fluentui/react-icons";
import { getGlpiClient } from "../services/glpiClient";
import { GlpiTicketSearchRow, TicketStatus } from "../types/glpi";
import { TICKET_STATUS_SHORT } from "../types/glpiConstants";

interface Props {
  selectedId: number | null;
  onSelect: (id: number | null, title: string) => void;
}

const useStyles = makeStyles({
  results: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    maxHeight: "240px",
    overflowY: "auto",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  item: {
    padding: "8px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    borderBottomColor: tokens.colorNeutralStroke3,
    borderBottomStyle: "solid",
    borderBottomWidth: "1px",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  itemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
});

export function TicketSearch({ selectedId, onSelect }: Props): React.ReactElement {
  const styles = useStyles();
  const [query, setQuery] = React.useState("");
  const [rows, setRows] = React.useState<GlpiTicketSearchRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setRows([]);
      setError(null);
      return;
    }
    const client = getGlpiClient();
    if (!client) return;

    let cancelled = false;
    const handle = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      client
        .searchTickets(trimmed)
        .then((res) => {
          if (!cancelled) setRows(res?.data ?? []);
        })
        .catch((e: Error) => {
          if (!cancelled) setError(e.message);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [query]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <Field label="Rechercher un ticket" hint="Par ID (#1234) ou mots-clés du titre">
        <Input
          value={query}
          onChange={(_, d) => setQuery(d.value)}
          contentBefore={<Search20Regular />}
          contentAfter={loading ? <Spinner size="tiny" /> : undefined}
          placeholder="ex: #1234 ou imprimante"
        />
      </Field>

      {error && <Caption1 style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Caption1>}

      {rows.length > 0 && (
        <div className={styles.results} role="listbox">
          {rows.map((row) => {
            const id = Number(row["2"]);
            const name = String(row["1"] ?? "");
            const status = Number(row["12"]) as TicketStatus;
            const entityName = row["80"] ? String(row["80"]) : "";
            const selected = id === selectedId;
            return (
              <div
                key={id}
                role="option"
                aria-selected={selected}
                tabIndex={0}
                className={mergeClasses(styles.item, selected && styles.itemSelected)}
                onClick={() => onSelect(id, name)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(id, name);
                  }
                }}
              >
                <div className={styles.row}>
                  <Badge appearance="outline" size="small">#{id}</Badge>
                  <Badge appearance="filled" color={statusColor(status)} size="small">
                    {TICKET_STATUS_SHORT[status] ?? "?"}
                  </Badge>
                  {entityName && (
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{entityName}</Caption1>
                  )}
                </div>
                <Body1>{name}</Body1>
              </div>
            );
          })}
        </div>
      )}

      {query.trim().length >= 2 && rows.length === 0 && !loading && !error && (
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Aucun ticket trouvé.</Caption1>
      )}
    </div>
  );
}

function statusColor(status: TicketStatus): "informative" | "warning" | "success" | "danger" | "subtle" {
  switch (status) {
    case 1:
      return "informative";
    case 2:
    case 3:
      return "warning";
    case 4:
      return "subtle";
    case 5:
      return "success";
    case 6:
      return "subtle";
    default:
      return "subtle";
  }
}
